import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isWithinInterval from 'date-fns/isWithinInterval';
import parseISO from 'date-fns/parseISO';
import { FormattedMessage, FormattedTime, useIntl } from 'react-intl';
import {
  Checkmark as CheckmarkIcon,
  ClockAlt as ClockIcon,
  Close as CloseIcon,
  Station as StationIcon
} from '@urbaninfrastructure/react-icons';
import { H2, Text, Box, Flex, Link, Anchor, Time } from '../../components';
import * as messages from './messages';
import {
  systemAlertsQuery_systemAlerts as SystemAlertType,
  systemAlertsQuery_systemAlerts_updates as UpdateType
} from '../../core-types';

type Props = {
  alert: SystemAlertType;
  highlight?: boolean;
};

type UpdateItemType = {
  createdAt: CORE_IsoDateTime;
  id: string;
  order: number;
  resolvesAlert: boolean;
  title: string;
};

const TimeComponent = ({ from, to }) => {
  const now = new Date();

  if (from) {
    if (isBefore(now, from)) {
      return (
        <Time
          upperCaseFirst
          detailedDate
          hideTime
          threshold={12}
          value={from}
        />
      );
    }

    if (to) {
      if (isWithinInterval(now, { start: from, end: to })) {
        return (
          <Time
            upperCaseFirst
            detailedDate
            hideTime
            threshold={12}
            futureDateMessage={
              <FormattedMessage {...messages.systemStatus.untilMessage} />
            }
            value={to}
          />
        );
      }
    }

    return (
      <Time upperCaseFirst detailedDate hideTime threshold={12} value={from} />
    );
  }
};

const SystemAlert = ({ alert, highlight = false }: Props) => {
  const intl = useIntl();
  const { dockGroups, resolvedAt, updates } = alert;

  const { slug } = alert;
  const linkParams = {
    statusId: alert.id,
    subSlug: slug && slug.current ? slug.current : null
  };

  const now = new Date();
  const incidentStartTime = parseISO(alert.incidentStartTime);
  const incidentEndTime =
    alert.incidentEndTime && parseISO(alert.incidentEndTime);
  const showWarning =
    alert.systemAlertType === 'system_closure' &&
    !resolvedAt &&
    (incidentStartTime && incidentEndTime
      ? isWithinInterval(now, {
          start: incidentStartTime,
          end: incidentEndTime
        })
      : isAfter(now, incidentStartTime));

  const updatesList: UpdateItemType[] =
    updates && updates.length
      ? updates
          .filter((update): update is UpdateType => Boolean(update))
          .map(({ id, createdAt, title }) => ({
            createdAt,
            id,
            order: Number(new Date(createdAt)),
            resolvesAlert: false,
            title: title || ''
          }))
      : [];

  if (resolvedAt !== null) {
    updatesList.push({
      createdAt: resolvedAt,
      order: Number(new Date(resolvedAt)),
      id: 'RESOLVED_AT',
      resolvesAlert: true,
      title: intl.formatMessage(messages.systemStatus.concluded)
    });
  }

  updatesList.sort((a, b) => b.order - a.order);

  return (
    <Flex
      px="sm"
      pt={highlight ? 'md' : 'sm'}
      pb={highlight ? 'md' : 'xs'}
      bg={highlight ? 'neutral.6' : 'transparent'}
      justifyContent="center"
    >
      <Box
        color={highlight ? 'white' : 'black'}
        width={{ xxs: 1, xl: 3 / 4, xxl: 1 / 2 }}
      >
        {showWarning && (
          <Box mb="xs">
            <CloseIcon size="53px" color="state.error" />
          </Box>
        )}
        <Text typoStyle="xxs" mb="xxs">
          {TimeComponent({
            from: incidentStartTime,
            to: incidentEndTime
          })}
        </Text>
        <Link route="system-status.status" passHref params={linkParams}>
          <Anchor
            color={highlight ? 'white' : 'black'}
            textDecoration={highlight ? 'none' : 'underline'}
          >
            <H2 typoStyle="md" mb="xs">
              {alert.title}
            </H2>
          </Anchor>
        </Link>
        <Text>{alert.description}</Text>
        {dockGroups && dockGroups.length === 1 && dockGroups[0] && (
          <Text typoStyle="xxs" mt="xxs">
            <Flex alignItems="top" justifyContent="center">
              <StationIcon size="16px" mt={1} />

              <Text as="span" typoStyle="xxs" pl={3}>
                <FormattedMessage
                  {...messages.systemStatus.oneStationAffected}
                  values={{
                    stationName: dockGroups[0].title
                  }}
                />
              </Text>
            </Flex>
          </Text>
        )}
        {dockGroups && dockGroups.length > 1 && (
          <Text typoStyle="xxs" mt="xxs">
            <Flex alignItems="top" justifyContent="center">
              <StationIcon size="16px" mt={1} />

              <Text as="span" typoStyle="xxs" pl={3}>
                <FormattedMessage
                  {...messages.systemStatus.manyStationsAffected}
                  values={{
                    stationCount: dockGroups.length
                  }}
                />
              </Text>
            </Flex>
          </Text>
        )}

        {updatesList.length > 0 && (
          <Box display="inline-block" mx="auto" mt="xxs">
            <Flex column justifyContent="left">
              {updatesList.map((update, i) => (
                <Text key={update.id} mt="xxs">
                  <Flex alignItems="top">
                    {update.resolvesAlert && (
                      <CheckmarkIcon size="20px" mt={1} color="state.success" />
                    )}

                    {!update.resolvesAlert && (
                      <ClockIcon
                        size="20px"
                        mt={1}
                        color={!resolvedAt && i === 0 ? '' : 'neutral.4'}
                      />
                    )}

                    <Text as="span" px={2}>
                      <FormattedTime value={update.createdAt} hour12={false} />
                    </Text>

                    <Text as="span" textAlign="left">
                      {update.title}
                    </Text>
                  </Flex>
                </Text>
              ))}
            </Flex>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default SystemAlert;
